// Helpers for browsing external Supabase projects connected via the
// governance Integrations hub. Consumed by gov's data-external page
// and by the editor CMS — both talk to the same edge functions
// (integration-admin + external-supabase-query) via sdk-bridge, so no
// service-role key ever reaches the browser.
//
// State lives on the consumer's root state under the `_ext*` prefix
// so it doesn't clash with app state. Callers invoke these through
// `el.call('loadRemoteProjects', s.root)` etc.

import {
  listExternalSupabaseProjects,
  introspectExternalSupabase,
  selectExternalSupabase,
  insertExternalSupabase,
  updateExternalSupabase,
  deleteExternalSupabase
} from '../integrations/index.js'
import { createClient } from '@supabase/supabase-js'

export const REMOTE_PAGE_SIZE = 50

// Per-project realtime client pool — one ws connection per slug,
// re-used across table subscriptions. Re-created when anon_key
// rotates.
const _realtimeClients = new Map()

export const getRemoteRealtimeClient = function getRemoteRealtimeClient(slug, url, anonKey) {
  if (!url || !anonKey) return null
  const existing = _realtimeClients.get(slug)
  if (existing?.url === url && existing?.anonKey === anonKey) return existing.client
  const client = createClient(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false }
  })
  _realtimeClients.set(slug, { url, anonKey, client })
  return client
}

export const tearDownRemoteRealtime = function tearDownRemoteRealtime(slug) {
  const entry = _realtimeClients.get(slug)
  if (!entry) return
  try { entry.client.removeAllChannels() } catch {}
  _realtimeClients.delete(slug)
}

// URL shape — gov uses /data/external, CMS wraps it however it wants
// (the base path is passed by the caller through `basePath`).
export const parseRemotePath = function parseRemotePath() {
  if (typeof window === 'undefined') return { slug: null, table: null }
  const params = new URLSearchParams(window.location.search || '')
  return {
    slug: params.get('slug') || null,
    table: params.get('t') || null
  }
}

export const makeRemoteUrl = function makeRemoteUrl(basePath, { slug, table }) {
  const params = new URLSearchParams()
  if (slug) params.set('slug', slug)
  if (table) params.set('t', table)
  const qs = params.toString()
  return `${basePath}${qs ? `?${qs}` : ''}`
}

export const loadRemoteProjects = async function loadRemoteProjects(root) {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._extProjectsWatcher) {
      root._extProjectsWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._extProjectsWatcher)
          root._extProjectsWatcher = null
          loadRemoteProjects(root)
        }
      }, 300)
    }
    return
  }
  root.update({ _extLoading: true })
  const res = await listExternalSupabaseProjects(orgId)
  root.update({
    _extProjects: res?.result?.projects || [],
    _extError: res?.error || null,
    _extLoading: false
  })
}

export const loadRemoteSchema = async function loadRemoteSchema(root, slug) {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId || !slug) {
    if (!root._extSchemaWatcher) {
      root._extSchemaWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._extSchemaWatcher)
          root._extSchemaWatcher = null
          loadRemoteSchema(root, slug)
        }
      }, 300)
    }
    return
  }
  root.update({ _extSchemaLoading: true, _extSchemaError: null })
  const res = await introspectExternalSupabase(orgId, slug)
  root.update({
    _extSchemaBySlug: {
      ...(root._extSchemaBySlug || {}),
      [slug]: res?.result?.tables || []
    },
    _extSchemaError: res?.error || null,
    _extSchemaLoading: false
  })
}

export const loadRemoteRows = async function loadRemoteRows(root, slug, table, page = 0) {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId || !slug || !table) return
  root.update({ _extRowsLoading: true })
  const res = await selectExternalSupabase(orgId, slug, table, {
    limit: REMOTE_PAGE_SIZE,
    offset: page * REMOTE_PAGE_SIZE
  })
  root.update({
    _extRows: res?.result?.rows || [],
    _extRowsTotal: res?.result?.total ?? null,
    _extRowsPage: page,
    _extRowsError: res?.error || null,
    _extRowsLoading: false,
    _extCurrentTable: { slug, table }
  })
  const project = (root?._extProjects || []).find((p) => p.slug === slug)
  const anonKey = project?.config?.anon_key
  const url = project?.config?.url
  if (anonKey && url) {
    const client = getRemoteRealtimeClient(slug, url, anonKey)
    if (client) {
      try { client.removeAllChannels() } catch {}
      client
        .channel(`ext-${slug}-${table}`)
        .on('postgres_changes', { event: '*', schema: 'public', table }, () => {
          loadRemoteRows(root, slug, table, root._extRowsPage || 0)
        })
        .subscribe()
    }
  }
}

export const schemaForRemoteTable = function schemaForRemoteTable(root, slug, table) {
  const list = root?._extSchemaBySlug?.[slug] || []
  return list.find((t) => t.name === table) || null
}

export const pkColumnOf = function pkColumnOf(schema) {
  const pk = (schema?.columns || []).find((c) => c.is_primary_key)
  return pk?.name || (schema?.columns || []).find((c) => c.name === 'id')?.name || null
}

export const coerceRemoteValue = function coerceRemoteValue(raw, col) {
  if (raw === '' || raw == null) {
    if (col.nullable) return null
    return raw
  }
  const t = col.type
  if (t === 'integer' || t === 'number') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : raw
  }
  if (t === 'boolean') return raw === 'true' || raw === true
  if (col.format === 'json' || col.format === 'jsonb' || t === 'object') {
    try { return JSON.parse(raw) } catch { return raw }
  }
  return raw
}

// Kick off the right fetches based on the current URL shape.
// Idempotent — repeated calls with unchanged state do nothing.
export const reactToRemotePath = function reactToRemotePath(root) {
  const { slug, table } = parseRemotePath()
  if (!slug) {
    if (!root._extProjects) loadRemoteProjects(root)
    return
  }
  if (slug && !table) {
    if (!root._extProjects) loadRemoteProjects(root)
    if (!root._extSchemaBySlug?.[slug]) loadRemoteSchema(root, slug)
    return
  }
  if (slug && table) {
    if (!root._extProjects) loadRemoteProjects(root)
    if (!root._extSchemaBySlug?.[slug]) loadRemoteSchema(root, slug)
    const cur = root._extCurrentTable
    if (cur?.slug !== slug || cur?.table !== table) {
      loadRemoteRows(root, slug, table, 0)
    }
  }
}

// Submit the row editor (insert or update) against the active table.
export const saveRemoteRow = async function saveRemoteRow(root) {
  const ed = root?._extEditor
  if (!ed) return { ok: false, error: 'no editor state' }
  const schema = schemaForRemoteTable(root, ed.slug, ed.table)
  const cols = schema?.columns || []
  const orgId = root?.jwtClaims?.workspace_org_id
  root.update({ _extEditorSaving: true })

  const payload = {}
  for (const c of cols) {
    const raw = ed.values?.[c.name]
    if (ed.mode === 'insert' && (raw == null || raw === '')) {
      if (c.default != null || c.is_primary_key) continue
    }
    payload[c.name] = coerceRemoteValue(raw, c)
  }

  let res
  if (ed.mode === 'insert') {
    res = await insertExternalSupabase(orgId, ed.slug, ed.table, payload)
  } else {
    const pk = pkColumnOf(schema)
    if (!pk) {
      root.update({ _extEditorSaving: false })
      return { ok: false, error: 'no primary key' }
    }
    const pkValue = ed.original?.[pk]
    const patch = { ...payload }
    delete patch[pk]
    res = await updateExternalSupabase(orgId, ed.slug, ed.table, [
      { column: pk, op: 'eq', value: pkValue }
    ], patch)
  }

  root.update({ _extEditorSaving: false })
  if (res?.ok) {
    root.update({ _extEditor: null })
    loadRemoteRows(root, ed.slug, ed.table, root?._extRowsPage || 0)
  }
  return res
}

export const deleteRemoteRow = async function deleteRemoteRow(root) {
  const ed = root?._extEditor
  if (!ed || ed.mode !== 'update') return { ok: false, error: 'not update mode' }
  const schema = schemaForRemoteTable(root, ed.slug, ed.table)
  const pk = pkColumnOf(schema)
  if (!pk) return { ok: false, error: 'no primary key' }
  const pkValue = ed.original?.[pk]
  if (pkValue == null) return { ok: false, error: 'no pk value' }
  const orgId = root?.jwtClaims?.workspace_org_id
  const res = await deleteExternalSupabase(orgId, ed.slug, ed.table, [
    { column: pk, op: 'eq', value: pkValue }
  ])
  if (res?.ok) {
    root.update({ _extEditor: null })
    loadRemoteRows(root, ed.slug, ed.table, root?._extRowsPage || 0)
  }
  return res
}
